import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface YouchuanPanParams {
  /**
   * 任务ID
   */
  jobId: string;
  /**
   * 图像编号，必须是0、1、2或3
   * @values 0 | 1 | 2 | 3
   */
  imageNo: 0 | 1 | 2 | 3;
  /**
   * 平移方向，支持: up, down, left, right
   * @values up | down | left | right
   */
  direction: 'up' | 'down' | 'left' | 'right';
  /**
   * 重混提示词，长度必须在1-8192字符之间
   */
  remixPrompt?: string;
  /**
   * 回调URL，必须是有效的HTTP/HTTPS URL
   */
  callback?: string;
}

export interface YouchuanPanResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * youchuan_pan
 *
 * Youchuan Pan Image Generator
 *
 * Image panning (directional extension) implementation for Youchuan Open Platform (悠船开放平台).
 *
 * API Documentation: https://tob.youchuan.cn/openapi/apis/%E5%9B%BE%E7%89%87%E7%94%9F%E6%88%90/api_pan
 *
 * @example
 * ```typescript
 * import { initSeacloud, youchuanPan } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await youchuanPan({
 *   jobId: 'your-job-id',
 *   imageNo: 0,
 *   direction: 'right'
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 YouchuanPanParams 接口定义
 * @returns 生成的资源列表
 */
export async function youchuanPan(
  params: YouchuanPanParams,
): Promise<YouchuanPanResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'youchuan_pan',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: YouchuanPanResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
